import { Box, Button, Group, Paper, SimpleGrid, Skeleton, Stack, Table, Text } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import type { Aufgabe, BereichLead, BereichResponse } from "@msc/shared";
import { useQueryClient } from "@tanstack/react-query";
import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";

import { AUFGABEN_KEY, BEREICH_KEY } from "../../hooks/useAufgaben.js";
import { useDecideExemption } from "../../hooks/useVisitExemptions.js";
import { ApiErrorAlert } from "../ApiErrorAlert.js";
import { QuelleTag, ZielKnopf, aufgabeFrist, aufgabeText, aufgabeWas } from "./AufgabeZeile.js";

type Zaehler = "todosUeberfaellig" | "besucheFaellig" | "systemstandUeberfaellig" | "statusberichteOffen";

const SPALTEN: Array<{ feld: Zaehler; kachel: string; spalte: string }> = [
  { feld: "todosUeberfaellig", kachel: "todos", spalte: "todos" },
  { feld: "besucheFaellig", kachel: "besuche", spalte: "besuche" },
  { feld: "systemstandUeberfaellig", kachel: "systemstand", spalte: "systemstand" },
  { feld: "statusberichteOffen", kachel: "statusberichte", spalte: "statusberichte" },
];

/** Werte über null stehen rot: dort hängt etwas. */
function zahlStil(n: number) {
  return n > 0 ? { color: "var(--msc-dringend)", fontWeight: 600 } : undefined;
}

/**
 * „Mein Bereich" für Führung: Summen als Kacheln, je Service Lead eine
 * Tabellenzeile mit aufklappbaren überfälligen Punkten (Reihenfolge der Leads
 * liefert die API), darunter die Freigaben mit Antragsteller.
 */
export function BereichSicht({
  data,
  isLoading,
  error,
  heute = new Date(),
}: {
  data: BereichResponse | undefined;
  isLoading: boolean;
  error: unknown;
  heute?: Date;
}) {
  const { t } = useTranslation();
  const [offen, setOffen] = useState<string | null>(null);

  if (error) return <ApiErrorAlert error={error} />;
  if (isLoading || !data) return <Skeleton h={240} radius="md" />;

  const summe = (f: Zaehler) => data.leads.reduce((s, l) => s + l[f], 0);

  return (
    <Stack gap="lg">
      <SimpleGrid cols={{ base: 1, xs: 2, md: 4 }} spacing="md">
        {SPALTEN.map((s) => (
          <Paper key={s.kachel} withBorder radius="md" p="md" data-kachel={s.kachel}>
            <Text size="sm" c="dimmed">
              {t(`aufgaben.bereich.kachel.${s.kachel}`)}
            </Text>
            <Text fz={30} fw={700} mt={4} style={zahlStil(summe(s.feld))}>
              {summe(s.feld)}
            </Text>
          </Paper>
        ))}
      </SimpleGrid>

      <Paper component="section" withBorder radius="md" style={{ overflow: "hidden" }}>
        <Text fw={600} px="md" py="sm">
          {t("aufgaben.bereich.tabelleTitel")}
        </Text>
        {data.leads.length === 0 ? (
          <Text size="sm" c="dimmed" px="md" pb="md">
            {t("aufgaben.bereich.keineLeads")}
          </Text>
        ) : (
          <Table.ScrollContainer minWidth={640}>
            <Table verticalSpacing="sm" horizontalSpacing="md">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t("aufgaben.bereich.spalte.lead")}</Table.Th>
                  {SPALTEN.map((s) => (
                    <Table.Th key={s.spalte}>{t(`aufgaben.bereich.spalte.${s.spalte}`)}</Table.Th>
                  ))}
                  <Table.Th>
                    <Box component="span" style={{ position: "absolute", width: 1, height: 1, overflow: "hidden" }}>
                      {t("aufgaben.bereich.spalte.aktion")}
                    </Box>
                  </Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {data.leads.map((l) => (
                  <LeadZeile
                    key={l.employeeId}
                    lead={l}
                    offen={offen === l.employeeId}
                    umschalten={() => setOffen(offen === l.employeeId ? null : l.employeeId)}
                    heute={heute}
                  />
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Paper>

      <Paper component="section" withBorder radius="md" style={{ overflow: "hidden" }}>
        <Text fw={600} px="md" py="sm">
          {t("aufgaben.bereich.freigabenTitel")}
        </Text>
        {data.freigaben.length === 0 ? (
          <Text size="sm" c="dimmed" px="md" pb="md">
            {t("aufgaben.bereich.keineFreigaben")}
          </Text>
        ) : (
          data.freigaben.map((a) => <FreigabeZeile key={a.id} aufgabe={a} heute={heute} />)
        )}
      </Paper>
    </Stack>
  );
}

function LeadZeile({
  lead: l,
  offen,
  umschalten,
  heute,
}: {
  lead: BereichLead;
  offen: boolean;
  umschalten: () => void;
  heute: Date;
}) {
  const { t, i18n } = useTranslation();
  const ueberfaellig = l.items.filter((i) => i.dringlichkeit === "ueberfaellig");
  return (
    <Fragment>
      <Table.Tr bg={offen ? "var(--msc-flaeche-orange)" : undefined}>
        <Table.Td fw={500}>{l.name}</Table.Td>
        {SPALTEN.map((s) => (
          <Table.Td key={s.feld} style={zahlStil(l[s.feld])} c={l[s.feld] > 0 ? undefined : "dimmed"}>
            {l[s.feld]}
          </Table.Td>
        ))}
        <Table.Td ta="right">
          <Button
            variant="default"
            size="xs"
            onClick={umschalten}
            aria-expanded={offen}
            aria-label={
              offen
                ? t("aufgaben.bereich.zuklappenFuer", { name: l.name })
                : t("aufgaben.bereich.detailsFuer", { name: l.name })
            }
          >
            {offen ? t("aufgaben.bereich.zuklappen") : t("aufgaben.bereich.details")}
          </Button>
        </Table.Td>
      </Table.Tr>
      {offen ? (
        <Table.Tr bg="var(--msc-flaeche-orange)" data-details>
          <Table.Td colSpan={SPALTEN.length + 2}>
            <Stack gap="xs">
              {ueberfaellig.length === 0 ? (
                <Text size="sm">{t("aufgaben.bereich.nichtsUeberfaellig", { name: l.name })}</Text>
              ) : (
                <>
                  <Text size="sm" fw={600}>
                    {t("aufgaben.bereich.ueberfaelligBei", { name: l.name })}
                  </Text>
                  {ueberfaellig.map((a) => (
                    <Group key={a.id} gap="sm" wrap="wrap">
                      <QuelleTag quelle={a.quelle} />
                      <Text size="sm" style={{ flex: "1 1 240px" }}>
                        {aufgabeText(a, t, i18n.language)}
                        {a.kundeName ? (
                          <Text span c="dimmed">
                            {` · ${a.kundeName}`}
                          </Text>
                        ) : null}
                      </Text>
                      <Text size="sm" fw={500} style={{ color: "var(--msc-dringend)" }}>
                        {aufgabeFrist(a, heute, t)}
                      </Text>
                    </Group>
                  ))}
                  <Text size="sm" c="dimmed">
                    {t("aufgaben.bereich.hinweisMag", { name: l.name })}
                  </Text>
                </>
              )}
            </Stack>
          </Table.Td>
        </Table.Tr>
      ) : null}
    </Fragment>
  );
}

function FreigabeZeile({ aufgabe: a, heute }: { aufgabe: Aufgabe; heute: Date }) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const approve = useDecideExemption("approve");
  const reject = useDecideExemption("reject");
  const was = a.ziel.art === "freigabe_kampagne" ? aufgabeText(a, t, i18n.language) : (a.kundeName ?? "");
  const meta = [
    a.antragsteller ? t("aufgaben.herkunft.antragsteller", { name: a.antragsteller }) : t("aufgaben.herkunft.kampagne"),
    aufgabeFrist(a, heute, t),
  ]
    .filter(Boolean)
    .join(" · ");

  // Die Ausnahme-id steckt in der Aufgaben-id (`freigabe:<id>`).
  const exemptionId = a.id.slice("freigabe:".length);
  const optionen = {
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: BEREICH_KEY });
      void qc.invalidateQueries({ queryKey: AUFGABEN_KEY });
    },
    onError: () => notifications.show({ color: "red", message: t("aufgaben.bereich.entscheidenFehlgeschlagen") }),
  };

  return (
    <Group gap="md" wrap="wrap" px="md" py="sm" style={{ borderTop: "1px solid var(--msc-linie-fein)" }}>
      <QuelleTag quelle={a.quelle} />
      <Box style={{ flex: "1 1 260px", minWidth: 0 }}>
        <Text fw={500}>
          {a.ziel.art === "freigabe_kampagne"
            ? aufgabeText(a, t, i18n.language)
            : [aufgabeText(a, t, i18n.language), a.kundeName].filter(Boolean).join(": ")}
        </Text>
        <Text size="sm" c="dimmed">
          {meta}
        </Text>
      </Box>
      {a.ziel.art === "freigabe_besuch" ? (
        <Group gap="xs">
          <Button
            size="xs"
            loading={approve.isPending}
            disabled={reject.isPending}
            aria-label={t("aufgaben.bereich.freigebenFuer", { was })}
            onClick={() => approve.mutate({ id: exemptionId }, optionen)}
          >
            {t("aufgaben.bereich.freigeben")}
          </Button>
          <Button
            size="xs"
            variant="default"
            loading={reject.isPending}
            disabled={approve.isPending}
            aria-label={t("aufgaben.bereich.ablehnenFuer", { was })}
            onClick={() => reject.mutate({ id: exemptionId }, optionen)}
          >
            {t("aufgaben.bereich.ablehnen")}
          </Button>
        </Group>
      ) : (
        <ZielKnopf ziel={a.ziel} label={t("aufgaben.bereich.inInbox")} was={aufgabeWas(a, t, i18n.language)} />
      )}
    </Group>
  );
}
